import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import { Box, CircularProgress, IconButton, Slide, Typography, useMediaQuery } from '@mui/material';
import { Check, Close, PriorityHigh, Remove } from '@mui/icons-material';
import { useThemes } from '../Theme/theme';
const ErrorSplashSVG = () => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      bottom: '0',
      left: '0',
      zIndex: 1401,
      borderBottomLeftRadius: '8px',
      overflow: 'hidden',
    }} // Adjust position as needed
  >
    <circle cx="50" cy="100" r="50" fill="#d32f2f" />
    <circle cx="70" cy="70" r="10" fill="#e64a19" />
    <circle cx="30" cy="80" r="5" fill="#f57c00" />
  </svg>
);

const SuccessSplashSVG = () => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      bottom: '0',
      left: '0',
      zIndex: 1401,
      borderBottomLeftRadius: '8px',
      overflow: 'hidden',
    }}
  >
    <path d="M0,80 Q20,55 40,75 T70,55 Q90,45 100,80 L100,100 L0,100 Z" fill="#388e3c" />
    <circle cx="20" cy="70" r="6" fill="#4caf50" />
    <circle cx="45" cy="65" r="7" fill="#66bb6a" />
    <circle cx="65" cy="80" r="3" fill="#81c784" />
    <circle cx="75" cy="70" r="5" fill="#81c784" />
  </svg>
);

export const WarningSplashSVG = () => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      bottom: '0',
      left: '0',
      borderBottomLeftRadius: '8px',
      overflow: 'hidden',
    }}
  >
    <path d="M0,75 Q10,60 30,79 T60,55 Q68,40 100,69 L100,100 L0,100 Z" fill="#e66210" />
    <circle cx="15" cy="85" r="4" fill="#ff9800" />
    <circle cx="55" cy="40" r="6" fill="#f57c00" />
    <circle cx="40" cy="70" r="7" fill="#ffa726" />
    <circle cx="70" cy="90" r="4" fill="#ffb74d" />
    <circle cx="80" cy="40" rx="4" ry="8" fill="#ffb74d" />
  </svg>
);

let snackbarRef = null;
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

/**
 * A reusable snackbar notification component that displays success, error and warning messages
 * @component
 * @example
 * // Import and mount in App.js or root component
 * import SnackbarComponent from './SnackbarComponent';
 *
 * function App() {
 *   return (
 *     <>
 *       <SnackbarComponent />
 *       {/* Other components *\/}
 *     </>
 *   );
 * }
 *
 * // Then use the exported functions anywhere in your app:
 * showSuccessSnackbar('Operation successful');
 * showErrorSnackbar('Something went wrong');
 * showWarningSnackbar('Please check your input');
 *
 * @requires {@link showSuccessSnackbar}
 * @requires {@link showErrorSnackbar}
 * @requires {@link showWarningSnackbar}
 * @requires {@link closeSnackbar}
 */
function SnackbarComponent() {
  const { theme } = useThemes();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState('success');
  const position = {
    vertical: isXs ? 'top' : 'bottom',
    horizontal: isXs ? 'center' : 'right',
  };
  let navUrl;
  let [openTime, setOpenTime] = useState(0);
  let modals = [...document.querySelectorAll('.MuiModal-root, .MuiDialog-root')].filter((e) => !e.closest('.MuiPopover-root')).length;

  const handleOpen = (message, horizontal, vertical, type, title) => {
    navUrl = window.location.href;
    setOpenTime(Date.now());
    handleClose();
    setText({ message, key: new Date().getTime(), title });
    setSeverity(type);
    setOpen(true);
    // if (isXs)
    //   setPosition({
    //     vertical: vertical,
    //     horizontal: horizontal,
    //   });
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      // setOpen(false);
      return;
    }
    setOpen(false);
    setProgress(0);
    setOpenTime(0);
    navUrl = '';
  };
  useEffect(() => {
    if (severity !== 'success' && open == true ? navUrl !== window.location.href : false) {
      setTimeout(() => {
        handleClose();
      }, 250);
    }
  }, [window.location.href]);

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (open == true && severity == 'success') {
      let timer;
      severity == 'success'
        ? (timer = setInterval(() => {
            setProgress((prev) => prev + 1);
          }, 45))
        : (timer = setInterval(() => {
            setProgress((prev) => prev + 1.65);
          }, 101));
      if (severity == 'success' ? progress > 110 : progress >= 100) {
        setOpen(false);
      }
      return () => {
        clearInterval(timer);
      };
    }
  }, [open, progress, severity]);

  snackbarRef = {
    showSuccess: (message, horizontal, vertical, title) => handleOpen(message, horizontal, vertical, 'success', title),
    showError: (message, horizontal, vertical) => handleOpen(message, horizontal, vertical, 'error'),
    showWarning: (message, horizontal, vertical) => handleOpen(message, horizontal, vertical, 'warning'),
    handleClose: () => handleClose(),
  };

  return (
    <Snackbar
      id="snackbar"
      key={text.key}
      anchorOrigin={position}
      open={open}
      // autoHideDuration={severity == "error" ? null : 4000}
      onClose={handleClose}
      TransitionComponent={SlideTransition}
      TransitionProps={{ timeout: 300 }}
      sx={{
        transform: { xs: 'translateY(20px)', sm: 'translateY(-20px)' }, // Adjusts the position 20px up from the bottom
        marginBottom: '75px', // Add 20px margin from the bottom
      }}
    >
      <Box
        sx={{
          backgroundColor: severity == 'error' ? '#f44336' : severity == 'warning' ? '#ff9800' : '#4caf50',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          boxShadow: 3,
          // width: '100% ',
          maxWidth: { sm: '250px', md: '500px' },
        }}
      >
        {severity == 'error' ? <ErrorSplashSVG /> : severity == 'warning' ? <WarningSplashSVG /> : <SuccessSplashSVG />}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '40px',
              backgroundColor: severity == 'error' ? '#d32f2f' : severity == 'warning' ? '#f57c00' : '#388e3c',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              left: '10px',
              top: '-10px',
            }}
          >
            {severity == 'error' ? (
              <Remove sx={{ color: 'white' }} />
            ) : severity == 'warning' ? (
              <PriorityHigh sx={{ color: 'white' }} />
            ) : (
              <Check sx={{ color: 'white' }} />
            )}
          </Box>
          <Box sx={{ marginLeft: '48px', mt: 0, zIndex: 1402 }}>
            <Typography variant="h5" fontWeight="bold">
              {text?.title ? text?.title : severity == 'error' ? 'Oops !' : severity == 'warning' ? 'Oh !' : 'Success'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '15px', fontWeight: 600 }}>
              {text.message}
            </Typography>
          </Box>
        </Box>
        <Box position="relative" display="inline-flex" padding={'5px'}>
          <IconButton
            size="small"
            aria-label="close"
            onClick={handleClose}
            disableRipple={false}
            disableTouchRipple={false}
            sx={{
              border: '0.5px solid white',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <CircularProgress
            variant="determinate"
            thickness={2}
            value={progress}
            size={32}
            sx={{
              color: 'white',
              position: 'absolute',
            }}
          />
        </Box>
      </Box>
      {/* </Alert> */}
    </Snackbar>
  );
}

/**
 * Displays a success snackbar notification
 * @param {string} message - The message to display in the snackbar
 * @param {string} horizontal - The horizontal position of the snackbar ('left', 'center', 'right')
 * @param {string} vertical - The vertical position of the snackbar ('top', 'bottom')
 * @returns {void}
 * @example
 *  * // Show success message Default
 * showSuccessSnackbar('Operation successful!');
 *
 * // Show success message at bottom-right
 * showSuccessSnackbar('Operation successful!', 'right', 'bottom');
 *
 * // Show success message at top-center
 * showSuccessSnackbar('Profile updated', 'center', 'top');
 *
 * @throws {Error} If snackbarRef is not initialized
 * @see {@link showErrorSnackbar}
 * @see {@link showWarningSnackbar}
 * @todo Add support for custom duration
 */
export function showSuccessSnackbar(message, horizontal = '', vertical = '',title = '') {
  snackbarRef && snackbarRef.showSuccess(message, horizontal, vertical,title);
  // setTimeout(() => {
  //     snackbarRef.handleClose();
  // }, 5000);
}

/**
 * Displays an error snackbar notification
 * @param {string} message - The message to display in the snackbar
 * @param {string} horizontal - The horizontal position of the snackbar ('left', 'center', 'right')
 * @param {string} vertical - The vertical position of the snackbar ('top', 'bottom')
 * @returns {void}
 * @example
 * // Show error message Default
 * showSuccessSnackbar('Operation failed!');
 *
 * // Show error message at bottom-right
 * showErrorSnackbar('Operation failed!', 'right', 'bottom');
 *
 * // Show error message at top-center
 * showErrorSnackbar('Invalid input', 'center', 'top');
 *
 * @throws {Error} If snackbarRef is not initialized
 * @see {@link showSuccessSnackbar}
 * @see {@link showWarningSnackbar}
 * @todo Add support for custom duration
 */
export function showErrorSnackbar(message, horizontal = '', vertical = '') {
  snackbarRef && snackbarRef.showError(message, horizontal, vertical);
  // setTimeout(() => {
  //     snackbarRef.handleClose();
  // }, 15000);
}

/**
 * Displays a warning snackbar notification
 * @param {string} message - The message to display in the snackbar
 * @param {string} horizontal - The horizontal position of the snackbar ('left', 'center', 'right')
 * @param {string} vertical - The vertical position of the snackbar ('top', 'bottom')
 * @returns {void}
 * @example
 * // Show warning message at Default
 * showWarningSnackbar('Connection unstable');
 *
 * // Show warning message at bottom-right
 * showWarningSnackbar('Connection unstable', 'right', 'bottom');
 *
 * // Show warning message at top-center
 * showWarningSnackbar('Session expiring soon', 'center', 'top');
 *
 * @throws {Error} If snackbarRef is not initialized
 * @see {@link showSuccessSnackbar}
 * @see {@link showErrorSnackbar}
 * @todo Add support for custom duration
 */
export function showWarningSnackbar(message, horizontal = '', vertical = '', title = '') {
  snackbarRef && snackbarRef.showWarning(message, horizontal, vertical, title);
  // setTimeout(() => {
  //     snackbarRef.handleClose();
  // }, 15000);
}

/**
 * Closes any currently displayed snackbar
 * @returns {void}
 * @example
 * // Close the current snackbar
 * closeSnackbar();
 *
 * @throws {Error} If snackbarRef is not initialized
 * @see {@link showSuccessSnackbar}
 * @see {@link showErrorSnackbar}
 * @see {@link showWarningSnackbar}
 */
export function closeSnackbar() {
  snackbarRef && snackbarRef.handleClose();
}

export default SnackbarComponent;
